import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  groupType,
  deskType,
  floorType,
  floorSvg,
  sensorType,
  desks,
  sensors,
  floorsType,
} from "../../interfaces/groups";
import api from "./api.slice";
import { createAppAsyncThunk } from "../hooks";
import { baseEndpoint } from "../../api/endpoints";
import { getToken } from "../../api/getToken";
import { RootState } from "../store";

type Request = "getFloor" | "getFloors" | "getDesks" | "getSensors";
type RequestState = "pending" | "fulfilled" | "rejected";
type syncStatusType = "SUCCEEDED" | "FAILED" | "RUNNING" | "IDLE";
interface GroupStore {
  groups: groupType[];
  syncStatus: syncStatusType;
  floors: floorType[];
  sensors: sensors;
  desks: desks;
  floor: floorSvg;
  assignedSensors: sensorType[];
  requests: {
    [requestName in Request]: {
      statuses: Record<string, RequestState | undefined>;
      errors: Record<string, string>;
    };
  };
}

export const getFloor = createAppAsyncThunk<floorType, { id: string }>(
  "floors/getFloor",
  async ({ id }, { rejectWithValue }) => {
    try {
      const token = await getToken();
      const response = await fetch(`${baseEndpoint}/floors/${id}`, {
        headers: { authorization: token },
      });
      const data = await response.json();
      if (data.success) {
        return data.payload;
      } else {
        const errorMessage = data.title || "Failed to get floor data";
        return rejectWithValue(errorMessage);
      }
    } catch (error) {
      console.log("err", error);
      return rejectWithValue(error.message);
    }
  }
);

export const getFloors = createAppAsyncThunk<
  floorType[],
  { locationID: string }
>("floors/getFloors", async ({ locationID }, { rejectWithValue }) => {
  try {
    const token = await getToken();
    const response = await fetch(
      `${baseEndpoint}/floors?locationID=${locationID}`,
      {
        headers: { authorization: token },
      }
    );
    const data = await response.json();

    if (data.success) {
      return data.payload;
    } else {
      const errorMessage = data.title || "Failed to get data";
      return rejectWithValue(errorMessage);
    }
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const getDesks = createAppAsyncThunk<deskType[], { floorID: string }>(
  "desk/getDesks",
  async ({ floorID }, { rejectWithValue }) => {
    try {
      const token = await getToken();
      const [response, response2] = await Promise.all([
        fetch(`${baseEndpoint}/desks?floorID=${floorID}`, {
          headers: { authorization: token },
        }),
        fetch(
          `${baseEndpoint}/sensors?floorID=${floorID}&assigned=true&isFiltered=false`,
          {
            headers: { authorization: token },
          }
        ),
      ]);

      const [data, data2] = await Promise.all([
        response.json(),
        response2.json(),
      ]);

      if (data.success && data2.success) {
        const deskStatus = data.payload?.map((desk) => {
          let sensor;
          if (data2.payload !== null) {
            sensor = data2.payload.find(
              (sensor) => sensor.id === desk.sensorID
            );
          }

          const sensorState = sensor?.state;

          const sensorTimestamp = sensor?.timestamp;

          return {
            ...desk,
            status: desk.status
              ? desk.status
              : sensorState === 1
              ? "Occupied"
              : sensorState === 0
              ? "Unoccupied"
              : "No status",

            sensorID: desk.sensorID ? desk.sensorID : "No sensor",
            timestamp:
              desk.status === "Under Maintenance" || desk.status === "Blocked"
                ? desk.timestamp
                : sensorTimestamp,
          };
        });

        return deskStatus;
      } else {
        const error = data.title || "error getting data";
        return rejectWithValue(error);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getSensors = createAppAsyncThunk<
  sensorType[],
  { floorID: string }
>("sensors/getSensors", async ({ floorID }, { rejectWithValue }) => {
  try {
    const token = await getToken();
    const [response1, response2] = await Promise.all([
      fetch(
        `${baseEndpoint}/sensors?floorID=${floorID}&assigned=${true}&isFiltered=true`,
        {
          headers: { authorization: token },
        }
      ),
      fetch(
        `${baseEndpoint}/sensors?floorID=${floorID}&assigned=${false}&isFiltered=true`,
        {
          headers: { authorization: token },
        }
      ),
    ]);

    const [data1, data2] = await Promise.all([
      response1.json(),
      response2.json(),
    ]);
    if (data1.success && data2.success) {
      return [...(data1.payload || []), ...(data2.payload || [])];
    }
    return rejectWithValue("error getting data");
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

const initialState: GroupStore = {
  groups: [],
  floors: [],
  desks: null,
  syncStatus: "IDLE",
  floor: null,
  sensors: null,
  assignedSensors: [],
  requests: {
    getFloor: {
      statuses: {},
      errors: {},
    },
    getFloors: {
      statuses: {},
      errors: {},
    },
    getDesks: {
      statuses: {},
      errors: {},
    },
    getSensors: {
      statuses: {},
      errors: {},
    },
  },
};

const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    addFloor: (state, action) => {
      const { floor } = action.payload;

      state.floors = [...state.floors, floor];
    },
    deleteFloor: (state, action) => {
      const { floorID } = action.payload;
      const updatedFloors = state.floors.filter(
        (floor) => floor.id !== floorID
      );
      state.floors = updatedFloors;
    },

    editFloorData: (state, action) => {
      const { floorID, name, svgURL, live, amenities, position } =
        action.payload;
      state.floor = {
        ...state.floor,
        name,
        svgURL,
        live,
        amenities,
        position,
      };
      const idx = state.floors.findIndex((floor) => floor.id === floorID);
      state.floors[idx] = {
        ...state.floors[idx],
        name,
        svgURL,
        live,
        amenities,
        position,
      };
    },
    editFloorName: (state, action) => {
      const { floorID, editedName } = action.payload;
      state.floor = { ...state.floor, name: editedName };
      const idx = state.floors.findIndex((floor) => floor.id === floorID);
      state.floors[idx] = { ...state.floors[idx], name: editedName };
    },
    editFloorPositions: (state, action) => {
      const { positions } = action.payload;
      state.floors = state.floors.map((floor) => {
        const index = Object.keys(positions).find((el) => el === floor.id);
        if (index) {
          return { ...floor, position: positions[index] };
        }
        return floor;
      });
    },

    updateFloorData: (state, action) => {
      const { capacity, floorID, locationID, occupied } = action.payload;
      const updatedFloors = state.floors.map((floor) =>
        floor.id === floorID ? { ...floor, capacity, occupied } : floor
      );
      state.floor = { ...state.floor, capacity, occupied };
      state.floors = updatedFloors;
    },

    updateDeskData: (state, action) => {
      const { deskAlias, deskID, floorID, locationID, sensorID, timestamp } =
        action.payload;
      const sensorState = action.payload.state;

      const updatedDesk = state.desks?.[floorID]?.map((desk) =>
        desk.id === deskID
          ? {
              ...desk,
              sensorID: desk.sensorID ? desk.sensorID : "No sensor",
              status:
                desk.status === "Under Maintenance" || desk.status === "Blocked"
                  ? desk.status
                  : sensorState === 1
                  ? "Occupied"
                  : sensorState === 0
                  ? "Unoccupied"
                  : "No status",
              alias: deskAlias,
              timestamp:
                desk.status === "Under Maintenance" || desk.status === "Blocked"
                  ? desk.timestamp
                  : timestamp,
            }
          : desk
      );
      if (state.desks?.[floorID]) {
        state.desks[floorID] = updatedDesk;
      }
    },

    updateDeskStatus: (state, action) => {
      const { deskID, alias, status, sensorID, timestamp, floorID } =
        action.payload;
      const index = state.desks[floorID].findIndex((d) => d.id === deskID);
      state.desks[floorID][index] = {
        ...state.desks[floorID][index],
        status,
        timestamp,
      };
    },
    updateNumberOfUnavailableDesks: (state, action) => {
      const { unavailable, occupied, floorID } = action.payload;
      const updatedFloors = state.floors.map((floor) =>
        floor.id === floorID ? { ...floor, occupied, unavailable } : floor
      );
      state.floor = { ...state.floor, unavailable, occupied };
      state.floors = updatedFloors;
    },
    updateDeskInfoOnAssignSensor: (state, action) => {
      const {
        deskID,
        sensorID,
        timestamp,
        floorID,
        sensorStatus,
        sensorTimestamp,
      } = action.payload;

      const index = state.desks[floorID].findIndex((d) => d.id === deskID);
      const deskInfo = state.desks[floorID][index];
      state.desks[floorID][index] = {
        ...deskInfo,
        sensorID,
        status:
          deskInfo.status === "Under Maintenance" ||
          deskInfo.status === "Blocked"
            ? deskInfo.status
            : sensorStatus === 1
            ? "Occupied"
            : sensorStatus === 0
            ? "Unoccupied"
            : "No status",
        timestamp:
          deskInfo.status === "Under Maintenance" ||
          deskInfo.status === "Blocked"
            ? timestamp
            : sensorTimestamp,
      };
      // const idx =
      //   state.sensors?.[floorID].findIndex((d) => d.id === sensorID) || -1;
      // if (idx > -1) {
      //   state.sensors[floorID][idx] = {
      //     ...state.sensors[floorID][idx],
      //     timestamp: sensorTimestamp,
      //     floorID,
      //     assigned: false,
      //   };
      // }
    },
    updateDeskInfoOnUnassignSensor: (state, action) => {
      const { deskID, timestamp, floorID, sensorTimestamp, sensorID } =
        action.payload;
      const index = state.desks[floorID].findIndex((d) => d.id === deskID);
      const deskInfo = state.desks[floorID][index];
      state.desks[floorID][index] = {
        ...deskInfo,
        sensorID: "No sensor",
        status:
          deskInfo.status === "Under Maintenance" ||
          deskInfo.status === "Blocked"
            ? deskInfo.status
            : "No status",
        timestamp:
          deskInfo.status === "Under Maintenance" ||
          deskInfo.status === "Blocked"
            ? timestamp
            : sensorTimestamp,
      };

      // const idx =
      //   state.sensors?.[floorID].findIndex((d) => d.id === sensorID) || -1;
      // if (idx > -1) {
      //   state.sensors[floorID][idx] = {
      //     ...state.sensors[floorID][idx],
      //     timestamp: sensorTimestamp,
      //     floorID,
      //     assigned: false,
      //   };
      // }
    },
    updateSyncFlag: (state, action) => {
      const { flag } = action.payload;
      state.syncStatus = flag;
    },
    resetGetFloorStatus: (state, action: PayloadAction<{ id: string }>) => {
      state.requests.getFloor.statuses[action.payload.id] = undefined;
    },
    resetGetFloorsStatus: (
      state,
      action: PayloadAction<{ locationID: string }>
    ) => {
      state.requests.getFloors.statuses[action.payload.locationID] = undefined;
    },
    resetGetDesksStatus: (
      state,
      action: PayloadAction<{ floorID: string }>
    ) => {
      state.requests.getDesks.statuses[action.payload.floorID] = undefined;
    },
    resetGetSensorsStatus: (
      state,
      action: PayloadAction<{ floorID: string }>
    ) => {
      state.requests.getDesks.statuses[action.payload.floorID] = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getFloor.pending, (state, action) => {
      state.requests.getFloor.statuses[action.meta.arg.id] = "pending";
    });
    builder.addCase(getFloor.rejected, (state, action) => {
      state.requests.getFloor.statuses[action.meta.arg.id] = "rejected";
      state.requests.getFloor.errors[action.meta.arg.id] = action.error.message;
    });
    builder.addCase(getFloor.fulfilled, (state, action) => {
      const index = state.floors.findIndex(
        (floor) => floor.id === action.meta.arg.id
      );

      if (index > -1) {
        state.floors[index] = action.payload;
      } else {
        state.floors = [...state.floors, action.payload];
      }
      state.floor = action.payload;

      state.requests.getFloor.statuses[action.meta.arg.id] = "fulfilled";
    });

    builder.addCase(getFloors.pending, (state, action) => {
      state.requests.getFloors.statuses[action.meta.arg.locationID] = "pending";
    });
    builder.addCase(getFloors.rejected, (state, action) => {
      state.requests.getFloors.statuses[action.meta.arg.locationID] =
        "rejected";
      state.requests.getFloors.errors[action.meta.arg.locationID] =
        action.error.message;
    });
    builder.addCase(getFloors.fulfilled, (state, action) => {
      if (action.payload) {
        const floors: floorType[] = action.payload.filter(
          (item1) => !state.floors.some((item2) => item2.id === item1.id)
        );

        state.floors = [...state.floors, ...floors];
      }

      state.requests.getFloors.statuses[action.meta.arg.locationID] =
        "fulfilled";
    });

    builder.addCase(getDesks.pending, (state, action) => {
      state.requests.getDesks.statuses[action.meta.arg.floorID] = "pending";
    });
    builder.addCase(getDesks.rejected, (state, action) => {
      state.requests.getDesks.statuses[action.meta.arg.floorID] = "rejected";
      state.requests.getDesks.errors[action.meta.arg.floorID] =
        action.error.message;
    });
    builder.addCase(getDesks.fulfilled, (state, action) => {
      const desks: deskType[] = action.payload;

      const updatedDesks = desks.reduce((acc, obj) => {
        if (!acc[obj.floorID]) {
          acc[obj.floorID] = [];
        }
        acc[obj.floorID].push(obj);
        return acc;
      }, {});

      state.desks = { ...state.desks, ...updatedDesks };

      state.requests.getDesks.statuses[action.meta.arg.floorID] = "fulfilled";
    });

    builder.addCase(getSensors.pending, (state, action) => {
      state.requests.getSensors.statuses[action.meta.arg.floorID] = "pending";
    });
    builder.addCase(getSensors.rejected, (state, action) => {
      state.requests.getSensors.statuses[action.meta.arg.floorID] = "rejected";
      state.requests.getSensors.errors[action.meta.arg.floorID] =
        action.error.message;
    });
    builder.addCase(getSensors.fulfilled, (state, action) => {
      const sensor: sensorType[] = action.payload;
      const updatedSensors = sensor.reduce((acc, obj) => {
        const floorID = action.meta.arg.floorID;

        if (!acc[floorID]) {
          acc[floorID] = [];
        }
        acc[floorID]?.push(obj);
        return acc;
      }, {});

      state.sensors = { ...state.sensors, ...updatedSensors };

      state.requests.getSensors.statuses[action.meta.arg.floorID] = "fulfilled";
    });
  },
});

export const selectGetFloorStatus = (state: RootState, id: string) => {
  return state.groups.requests.getFloor.statuses[id];
};
export const selectGetFloorsStatus = (state: RootState, locationID: string) => {
  return state.groups.requests.getFloors.statuses[locationID];
};
export const selectGetDesksStatus = (state: RootState, floorID: string) => {
  return state.groups.requests.getDesks.statuses[floorID];
};
export const selectGetSensorsStatus = (state: RootState, floorID: string) => {
  return state.groups.requests.getSensors.statuses[floorID];
};

export const {
  updateFloorData,
  addFloor,
  deleteFloor,
  updateDeskData,
  editFloorData,
  updateDeskInfoOnUnassignSensor,
  updateDeskInfoOnAssignSensor,
  resetGetFloorStatus,
  resetGetFloorsStatus,
  resetGetDesksStatus,
  resetGetSensorsStatus,
  updateSyncFlag,
  updateDeskStatus,
  editFloorName,
  editFloorPositions,
  updateNumberOfUnavailableDesks,
} = groupsSlice.actions;
export default groupsSlice.reducer;
